/**
 * Shared state model for dynamically generated spreadsheet columns
 * (AI enrichment, Find/Verify Email, workspace agent results, etc.).
 */

/** @typedef {"pending" | "processing" | "completed" | "completed_with_issues" | "failed"} GeneratedColumnStatus */
/** @typedef {"queued" | "loading" | "success" | "error"} GeneratedCellStatus */

/** Minimum time skeletons stay visible after a generated column is added. */
export const GENERATED_COLUMN_MIN_LOADING_MS = 3000;

export const GENERATED_COLUMN_STATUS = Object.freeze({
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  COMPLETED_WITH_ISSUES: "completed_with_issues",
  FAILED: "failed",
});

export const GENERATED_CELL_STATUS = Object.freeze({
  QUEUED: "queued",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
});

/**
 * Gate that keeps generated-column cells in the loading/skeleton state for at
 * least `minMs`, even when the API/mock returns immediately.
 *
 * Usage:
 *   const gate = createGeneratedColumnLoadingGate();
 *   // set cells to running, kick off request…
 *   await gate.waitBeforeReveal();
 *   // then apply success / failure results
 */
export function createGeneratedColumnLoadingGate(
  minMs = GENERATED_COLUMN_MIN_LOADING_MS
) {
  const startedAt = Date.now();
  let waitPromise = null;

  return {
    startedAt,
    minMs,
    waitBeforeReveal() {
      if (!waitPromise) {
        waitPromise = new Promise((resolve) => {
          const remaining = Math.max(0, minMs - (Date.now() - startedAt));
          if (remaining <= 0) {
            resolve();
            return;
          }
          setTimeout(resolve, remaining);
        });
      }
      return waitPromise;
    },
  };
}

/** Map legacy EnrichmentGrid cell statuses onto the shared model. */
export function normalizeGeneratedCellStatus(status) {
  if (status === "running" || status === "loading") return GENERATED_CELL_STATUS.LOADING;
  if (status === "queued") return GENERATED_CELL_STATUS.QUEUED;
  if (status === "success") return GENERATED_CELL_STATUS.SUCCESS;
  if (status === "failed" || status === "missing" || status === "error") {
    return GENERATED_CELL_STATUS.ERROR;
  }
  return null;
}

/**
 * @param {Array<{ status?: string }|null|undefined>} cellEntries
 * @returns {GeneratedColumnStatus}
 */
export function deriveGeneratedColumnStatus(cellEntries) {
  const statuses = (Array.isArray(cellEntries) ? cellEntries : [])
    .map((entry) => normalizeGeneratedCellStatus(entry?.status))
    .filter(Boolean);

  if (!statuses.length) return GENERATED_COLUMN_STATUS.PENDING;

  const hasActive = statuses.some(
    (status) =>
      status === GENERATED_CELL_STATUS.QUEUED || status === GENERATED_CELL_STATUS.LOADING
  );
  if (hasActive) {
    const hasStarted = statuses.some((status) => status === GENERATED_CELL_STATUS.LOADING);
    return hasStarted
      ? GENERATED_COLUMN_STATUS.PROCESSING
      : GENERATED_COLUMN_STATUS.PENDING;
  }

  const errorCount = statuses.filter((status) => status === GENERATED_CELL_STATUS.ERROR).length;
  if (errorCount === statuses.length) return GENERATED_COLUMN_STATUS.FAILED;
  if (errorCount > 0) return GENERATED_COLUMN_STATUS.COMPLETED_WITH_ISSUES;
  return GENERATED_COLUMN_STATUS.COMPLETED;
}

export function isGeneratedColumnHighlightActive(columnStatus) {
  return (
    columnStatus === GENERATED_COLUMN_STATUS.PENDING
    || columnStatus === GENERATED_COLUMN_STATUS.PROCESSING
  );
}

/** True when a generated-column cell has a settled AI result (success or error). */
export function isGeneratedCellResultSettled(entry) {
  const status = normalizeGeneratedCellStatus(entry?.status);
  return (
    status === GENERATED_CELL_STATUS.SUCCESS
    || status === GENERATED_CELL_STATUS.ERROR
  );
}

/**
 * True when any enrichment action for `rowId` has a settled generated result.
 * Used to stroke the entire spreadsheet row.
 */
export function rowHasGeneratedContent(statusByAction, actionKeys, rowId) {
  if (!rowId || !Array.isArray(actionKeys) || actionKeys.length === 0) {
    return false;
  }
  return actionKeys.some((actionKey) =>
    isGeneratedCellResultSettled(statusByAction?.[actionKey]?.[rowId])
  );
}

/**
 * Toolbar progress copy for a generated column.
 * Examples:
 * - "AI Enrichment 1: Generating…"
 * - "AI Enrichment 1: 2 of 5 complete"
 * - "Verify Email: 4 complete, 1 issue"
 */
export function formatGeneratedColumnProgressLabel({
  label,
  completeCount = 0,
  issueCount = 0,
  totalCount = 0,
  running = false,
}) {
  const safeLabel = String(label || "Enrichment").trim() || "Enrichment";
  if (running) {
    if (completeCount <= 0) {
      return `${safeLabel}: Generating…`;
    }
    return `${safeLabel}: ${completeCount} of ${totalCount} complete`;
  }
  if (issueCount > 0) {
    const issueWord = issueCount === 1 ? "issue" : "issues";
    return `${safeLabel}: ${completeCount} complete, ${issueCount} ${issueWord}`;
  }
  return `${safeLabel}: ${completeCount} complete`;
}

/**
 * User-facing cell error copy — never expose raw API/mock infrastructure errors.
 * Controlled messages already written for the UI are preserved.
 */
export function formatGeneratedCellError(actionKey, error, options = {}) {
  const { formatVerifyEmailCellError } = options;
  if (actionKey === "verify_email" && typeof formatVerifyEmailCellError === "function") {
    return formatVerifyEmailCellError(error);
  }

  const raw = String(error || "").trim();
  if (!raw) {
    if (actionKey === "find_email") return "No result available";
    if (actionKey?.startsWith?.("ai_enrichment")) return "AI enrichment failed";
    return "No result available";
  }

  if (/No local mock|ECONNREFUSED|Network Error|Failed to fetch|ERR_NETWORK|ENOTFOUND|ERR_CONNECTION/i.test(raw)) {
    if (actionKey === "verify_email") return "Unable to verify";
    if (actionKey === "find_email") return "No result available";
    if (actionKey?.startsWith?.("ai_enrichment")) return "Unable to generate result";
    return "Unable to generate result";
  }

  // Preserve short, controlled UI messages; map a few internal codes.
  const looksTechnical =
    raw.length > 160
    || /[{[]/.test(raw)
    || /\b(stack|traceback|exception|TypeError|ReferenceError)\b/i.test(raw);

  if (!looksTechnical) {
    if (
      raw === "Empty AI response"
      || raw === "Item did not complete"
      || raw === "Polling aborted"
    ) {
      return "AI enrichment failed";
    }
    if (raw === "Failed" || raw === "Find Email failed") {
      return actionKey === "find_email" ? "No result available" : "AI enrichment failed";
    }
    if (raw === "Enrichment failed") {
      return actionKey?.startsWith?.("ai_enrichment")
        ? "AI enrichment failed"
        : "Unable to generate result";
    }
    return raw;
  }

  if (actionKey === "find_email") return "No result available";
  if (actionKey === "verify_email") return "Unable to verify";
  if (actionKey?.startsWith?.("ai_enrichment")) return "AI enrichment failed";
  return "Unable to generate result";
}

export const GENERATED_COLUMN_CELL_CLASS = "generated-column-cell";
export const GENERATED_COLUMN_PROCESSING_CLASS = "generated-column-processing";
export const GENERATED_COLUMN_HEADER_PROCESSING_CLASS = "generated-column-header-processing";
/** Settled AI result cell — light purple fill (header stays unstyled). */
export const GENERATED_COLUMN_RESULT_CLASS = "generated-column-result";
/** Row containing at least one settled AI result — primary purple stroke. */
export const GENERATED_ROW_HIGHLIGHT_CLASS = "generated-row-highlight";
